import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, uploadUrl } from "@/lib/api";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { buildDirectoryReminderUrl } from "@/lib/whatsapp";
import { PhotoUploadCrop } from "@/components/PhotoUploadCrop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Pencil, Save, X, Send, Copy, Camera, UploadCloud, Trash2, Loader2, Users2, Link as LinkIcon,
    BadgeCheck, ShieldCheck, QrCode, Download,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { buildRolePathChips, hasLeadershipPosition } from "@/lib/rolePaths";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

function parseDayMonth(value) {
    if (!value) return { day: "", month: "" };
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return { day: iso[3], month: MONTHS_SHORT[parseInt(iso[2], 10) - 1] || "" };
    const m = /^(\d{1,2})-([A-Z][a-z]{2})$/.exec(value);
    if (m) return { day: m[1].padStart(2, "0"), month: m[2] };
    return { day: "", month: "" };
}

function formatDDMon(value) {
    const { day, month } = parseDayMonth(value);
    return day && month ? `${day}-${month}` : "";
}

function DayMonthPicker({ value, onChange, testid }) {
    const parsed = parseDayMonth(value);
    const [day, setDay] = useState(parsed.day);
    const [month, setMonth] = useState(parsed.month);
    const lastRef = useRef(value || "");

    useEffect(() => {
        if ((value || "") !== lastRef.current) {
            const p = parseDayMonth(value);
            setDay(p.day);
            setMonth(p.month);
            lastRef.current = value || "";
        }
    }, [value]);

    const emit = (d, mo) => {
        const next = d && mo ? `${d}-${mo}` : "";
        lastRef.current = next;
        onChange(next);
    };

    return (
        <div className="grid grid-cols-2 gap-2">
            <Select value={day || undefined} onValueChange={(d) => { setDay(d); emit(d, month); }}>
                <SelectTrigger data-testid={`${testid}-day`}><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent className="max-h-64">
                    {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={month || undefined} onValueChange={(mo) => { setMonth(mo); emit(day, mo); }}>
                <SelectTrigger data-testid={`${testid}-month`}><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                    {MONTHS_SHORT.map((mo) => <SelectItem key={mo} value={mo}>{mo}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
    );
}

function initialsOf(name) {
    return (name || "?")
        .split(" ")
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

function PhotoBlock({ src, name, size = "h-28 w-28", testid }) {
    if (!src) {
        return (
            <div
                className={`${size} shrink-0 grid place-items-center rounded-2xl bg-rotary-blue text-white rotary-heading text-3xl`}
                data-testid={`${testid}-placeholder`}
            >
                {initialsOf(name) || "?"}
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={name || ""}
            className={`${size} shrink-0 rounded-2xl object-cover border`}
            data-testid={`${testid}-image`}
        />
    );
}

function EditPhotoBlock({ src, onChoose, onRemove, inputRef, onFile, testid }) {
    return (
        <div className="flex items-center gap-4">
            <div className="h-28 w-28 shrink-0 rounded-2xl border bg-muted/40 overflow-hidden grid place-items-center" data-testid={`${testid}-preview`}>
                {src ? (
                    <img src={src} alt="" className="h-full w-full object-cover" />
                ) : (
                    <Camera className="h-6 w-6 text-muted-foreground" />
                )}
            </div>
            <div className="flex flex-col gap-2">
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={onFile}
                    className="hidden"
                    data-testid={`${testid}-input`}
                />
                <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onChoose} className="rounded-full" data-testid={`${testid}-choose-btn`}>
                        <UploadCloud className="mr-1.5 h-4 w-4" /> {src ? "Replace" : "Upload"}
                    </Button>
                    {src && (
                        <Button
                            type="button" variant="outline" size="sm" onClick={onRemove}
                            className="rounded-full text-rotary-cranberry border-rotary-cranberry/40 hover:bg-rotary-cranberry hover:text-white"
                            data-testid={`${testid}-remove-btn`}
                        >
                            <Trash2 className="mr-1.5 h-4 w-4" /> Remove
                        </Button>
                    )}
                </div>
                <p className="text-[11px] text-muted-foreground">JPG or PNG</p>
            </div>
        </div>
    );
}

function DetailRow({ label, value, testid }) {
    return (
        <div className="rounded-xl border bg-white p-4">
            <span className="label-eyebrow">{label}</span>
            <p className="mt-1.5 text-sm text-rotary-ink break-words" data-testid={testid}>
                {value ? value : <span className="text-muted-foreground italic">Not provided</span>}
            </p>
        </div>
    );
}

export default function MemberProfileDialog({
    open,
    onOpenChange,
    member,             // { name, phone, email, birthday, address, spouse_*, year_role: {...}, ... }
    committees = [],
    onUpdated,          // (newMember) => void
    onEditCommittees,   // () => void  – opens the existing EditAssignmentsDialog
    onRemove,           // () => void  – removes the member from this year
    initialMode = "view",
    canWrite = true,
    onToggleVerified,
    // Iter 102 · Governor context used by the manual WhatsApp outreach
    // flow to sign the "please verify your profile" message. Blank
    // strings render the message with a neutral greeting.
    governorName = "",
    districtNumber = "",
}) {
    const [mode, setMode] = useState(initialMode);
    const [form, setForm] = useState({});
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [removePhoto, setRemovePhoto] = useState(false);
    const [spousePhotoFile, setSpousePhotoFile] = useState(null);
    const [spousePhotoPreview, setSpousePhotoPreview] = useState("");
    const [removeSpousePhoto, setRemoveSpousePhoto] = useState(false);
    const [saving, setSaving] = useState(false);
    const photoRef = useRef(null);
    const spouseRef = useRef(null);

    useEffect(() => {
        if (!open || !member) return;
        setMode(canWrite ? initialMode : "view");
        setForm({
            name: member.name || "",
            phone: String(member.phone || "").replace(/\D+/g, "").slice(-10),
            email: member.email || "",
            birthday: member.birthday || "",
            postal_address: member.address || member.postal_address || "",
            // Iter 76: classification is a free-text field, up to 70 chars,
            // written verbatim to Column G of the sheet. Loaded from the
            // existing member payload so admins never blank existing data
            // by opening the edit form.
            classification: member.classification || "",
            // Iter 82: club_name is a free-text field displayed on member
            // cards, list rows, profile page, and printed directory.
            club_name: member.club_name || "",
            spouse_name: member.spouse_name || "",
            spouse_birthday: member.spouse_birthday || "",
            anniversary: member.spouse_anniversary || member.anniversary || "",
            // Iter 100j · optional spouse contact fields. Excluded
            // from profile-completion — blank rows keep 100%.
            spouse_phone: member.spouse_phone || "",
            spouse_email: member.spouse_email || "",
        });
        setPhotoFile(null);
        setPhotoPreview("");
        setRemovePhoto(false);
        setSpousePhotoFile(null);
        setSpousePhotoPreview("");
        setRemoveSpousePhoto(false);
    }, [open, member]);

    if (!member) return null;

    const yr = member.year_role || {};
    const yrid = yr.id;
    const formToken = yr.form_token || "";
    const progress = Number.isFinite(yr.progress_pct) ? yr.progress_pct : 0;
    const submitted = !!yr.submitted_at;

    // Full committee-path role chips — each unique
    // "Position · 1st Level Committee · SubCommittee · 2nd Level SubCommittee"
    // path is shown exactly once. Purely visual; never mutates role data.
    const roleChips = buildRolePathChips(member, committees);
    const isLeadership = hasLeadershipPosition(member);

    const photoSrc = mode === "edit"
        ? (photoPreview || (removePhoto ? "" : (member.photo_filename ? uploadUrl(member.photo_filename, member.updated_at) : "")))
        : (member.photo_filename ? uploadUrl(member.photo_filename, member.updated_at) : "");
    const spouseSrc = mode === "edit"
        ? (spousePhotoPreview || (removeSpousePhoto ? "" : (member.spouse_photo_filename ? uploadUrl(member.spouse_photo_filename, member.updated_at) : "")))
        : (member.spouse_photo_filename ? uploadUrl(member.spouse_photo_filename, member.updated_at) : "");

    const handleFile = (e, isSpouse) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
            toast.error("Photo must be JPG or PNG");
            e.target.value = "";
            return;
        }
        const preview = URL.createObjectURL(file);
        if (isSpouse) { setSpousePhotoFile(file); setSpousePhotoPreview(preview); setRemoveSpousePhoto(false); }
        else { setPhotoFile(file); setPhotoPreview(preview); setRemovePhoto(false); }
    };

    // Called by PhotoUploadCrop with the already-cropped 272×272 JPEG File.
    const acceptCroppedPhoto = (file, isSpouse) => {
        const preview = URL.createObjectURL(file);
        if (isSpouse) { setSpousePhotoFile(file); setSpousePhotoPreview(preview); setRemoveSpousePhoto(false); }
        else { setPhotoFile(file); setPhotoPreview(preview); setRemovePhoto(false); }
    };

    const save = async () => {
        if (!yrid) return;
        // Strict 10-digit phone (iter 40). Empty is allowed — treated as
        // "no change"; the backend keeps the existing phone.
        if (form.phone && String(form.phone).replace(/\D+/g, "").length !== 10) {
            toast.error("Phone must be exactly 10 digits (no country code, no spaces).");
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
            if (photoFile) fd.append("photo", photoFile);
            if (spousePhotoFile) fd.append("spouse_photo", spousePhotoFile);
            fd.append("remove_photo", removePhoto ? "true" : "false");
            fd.append("remove_spouse_photo", removeSpousePhoto ? "true" : "false");
            const { data } = await api.post(`/scoped/year-roles/${yrid}/edit`, fd);
            toast.success("Profile updated");
            onUpdated?.(data);
            setMode("view");
            setPhotoFile(null); setPhotoPreview(""); setRemovePhoto(false);
            setSpousePhotoFile(null); setSpousePhotoPreview(""); setRemoveSpousePhoto(false);
        } catch (err) {
            const d = err?.response?.data?.detail;
            const msg = Array.isArray(d) ? d.map((e) => e?.msg || JSON.stringify(e)).join(" · ") : (d || "Save failed");
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const copyFormLink = async () => {
        if (!formToken) { toast.error("No form link for this member yet"); return; }
        const link = `${window.location.origin}/form/${formToken}`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Personal form link copied");
        } catch {
            toast.error("Could not copy link");
        }
    };

    // Iter 104 · Hybrid WhatsApp outreach. Try the AiSensy campaign
    // send on the backend first; on failure (missing key, provider
    // rejection, network error) auto-open the wa.me link so the admin
    // never leaves the "profile update" workflow empty-handed. The
    // legacy Gupshup / Twilio branches remain in the codebase behind
    // their own `is_enabled` guards but are bypassed here.
    //
    // Iter 109 · Sync-popup trick — browsers block `window.open` fired
    // AFTER an awaited fetch (user gesture is lost). We open a blank
    // popup synchronously inside the click handler, then either close
    // it on success or navigate it to wa.me on failure. Toast also gets
    // a clickable action so the admin can always retry the manual link.
    const openHybridWhatsApp = async () => {
        if (!yrid) {
            toast.error("No form link generated for this member yet");
            return;
        }
        const memberPhone = String(member?.phone || form.phone || "").replace(/\D+/g, "");
        if (!memberPhone) {
            toast.error("Member phone missing — can't send WhatsApp");
            return;
        }
        const localFallbackUrl = buildDirectoryReminderUrl({
            governorName,
            districtNumber,
            memberName: member?.name,
            phone: memberPhone,
            token: formToken,
        });
        // Reserve the popup NOW while the click is still a fresh user
        // gesture. Firefox/Chrome allow this even from a subsequent
        // async continuation — we just have to have opened it before
        // the first `await`. On success we close it silently.
        const popup = window.open("", "_blank");
        try {
            const { data } = await api.post(`/scoped/year-roles/${yrid}/whatsapp-quick-send`);
            if (data?.ok && data?.channel === "aisensy") {
                if (popup && !popup.closed) popup.close();
                toast.success(`WhatsApp delivered to ${member?.name || "member"}`, {
                    description: `Sent via AiSensy to +91 ${memberPhone.slice(-10)} · they'll get it within a few seconds.`,
                    duration: 6000,
                    className: "border-2 border-emerald-400 bg-emerald-50 text-emerald-900 shadow-lg",
                    icon: "✅",
                });
                return;
            }
            const url = data?.wa_url || localFallbackUrl;
            if (popup && !popup.closed) popup.location = url;
            else window.open(url, "_blank", "noopener,noreferrer");
            toast("AiSensy skipped — opened manual WhatsApp", {
                description: String(data?.detail || "backend fell back to manual").slice(0, 200),
                duration: 7000,
                className: "border-2 border-amber-400 bg-amber-50 text-amber-900 shadow-lg",
                icon: "⚠️",
                action: { label: "Open again", onClick: () => window.open(url, "_blank", "noopener,noreferrer") },
            });
        } catch (err) {
            if (popup && !popup.closed) popup.location = localFallbackUrl;
            else window.open(localFallbackUrl, "_blank", "noopener,noreferrer");
            const detail = err?.response?.data?.detail || err?.message || "server error";
            toast("AiSensy request failed — opened manual WhatsApp", {
                description: String(detail).slice(0, 200),
                duration: 7000,
                className: "border-2 border-amber-400 bg-amber-50 text-amber-900 shadow-lg",
                icon: "⚠️",
                action: { label: "Open again", onClick: () => window.open(localFallbackUrl, "_blank", "noopener,noreferrer") },
            });
        }
    };
    // Preserve the older function name for the button handlers below.
    const openManualWhatsApp = openHybridWhatsApp;

    const downloadQR = async () => {
        if (!member?.id) return;
        try {
            const resp = await api.get(`/scoped/persons/${member.id}/qr`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "image/png" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(member.name || "member").replace(/[^A-Za-z0-9]+/g, "_")}_QR.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("QR code downloaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "QR download failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[92vh] overflow-y-auto"
                data-testid="member-profile-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="rotary-heading text-2xl flex items-center gap-2">
                        {mode === "edit" ? "Edit profile" : "Member profile"}
                    </DialogTitle>
                </DialogHeader>

                {/* Hero + actions */}
                <section className="rounded-2xl border bg-white p-5 mt-2">
                    <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                        {mode === "edit" ? (
                            <PhotoUploadCrop
                                subject="member"
                                previewSrc={photoSrc}
                                onCropped={(file) => acceptCroppedPhoto(file, false)}
                                onRemove={photoSrc ? (() => { setPhotoFile(null); setPhotoPreview(""); setRemovePhoto(true); }) : undefined}
                                testid="profile-edit-photo"
                                yearId={yr.id || yr._id || ""}
                            />
                        ) : (
                            <PhotoBlock src={photoSrc} name={member.name} testid="profile-photo" />
                        )}

                        <div className="flex-1 space-y-3 min-w-0">
                            <span className="label-eyebrow">Directory member</span>
                            <h2
                                className="rotary-heading text-3xl text-rotary-ink truncate flex items-center gap-2"
                                data-testid="profile-name"
                            >
                                <span className="truncate">{form.name || member.name || "—"}</span>
                                {!!yr.verified_at && (
                                    <BadgeCheck
                                        className="h-6 w-6 text-emerald-600 shrink-0"
                                        aria-label="Data verified"
                                        data-testid="profile-verified-badge"
                                    />
                                )}
                            </h2>
                            {member.club_name && (
                                <div className="flex flex-wrap items-center gap-2" data-testid="profile-role-club">
                                    <span
                                        className="rotary-chip"
                                        data-testid="profile-club-chip"
                                    >
                                        <span className="opacity-60 uppercase tracking-wider text-[10px] font-semibold mr-1">Home Club</span>
                                        {member.club_name}
                                    </span>
                                </div>
                            )}
                            <div className="flex flex-wrap gap-2" data-testid="profile-role-path-chips">
                                {roleChips.map((chip, idx) => (
                                    <span
                                        key={idx}
                                        className="rotary-chip text-[11px] bg-rotary-blue/10 text-rotary-blue border-rotary-blue/20"
                                        title={chip}
                                        data-testid={`profile-role-path-chip-${idx}`}
                                    >
                                        {chip}
                                    </span>
                                ))}
                                {isLeadership && (
                                    <span
                                        className="inline-flex items-center gap-1 rounded-full bg-rotary-gold/15 border border-rotary-gold/40 text-rotary-ink px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                                        data-testid="profile-club-leadership-tag"
                                        title={member.club_name ? `Club leader · ${member.club_name}` : "Club leader"}
                                    >
                                        <span className="whitespace-nowrap">Club leader</span>
                                    </span>
                                )}
                                {/* Status is telegraphed by the card background
                                    color on the parent list — no chip needed
                                    inside the profile view itself. */}
                                {false && submitted && (
                                    <span
                                        className="rotary-chip text-[11px] bg-emerald-50 text-emerald-700 border-emerald-200"
                                        data-testid="profile-submitted-chip"
                                        title="Member submitted the form"
                                    >
                                        Filled
                                    </span>
                                )}
                                {!!yr.verified_at && (
                                    <span
                                        className="rotary-chip text-[11px] bg-emerald-50 text-emerald-700 border-emerald-200"
                                        data-testid="profile-verified-chip"
                                        title={yr.verified_by ? `Verified by ${yr.verified_by}` : "Verified"}
                                    >
                                        <BadgeCheck className="h-3 w-3 mr-1" /> Verified
                                    </span>
                                )}
                            </div>
                            <div className="max-w-md space-y-1.5 pt-1">
                                <div className="flex items-center justify-between text-[11px]">
                                    <span className="label-eyebrow">Profile completion</span>
                                    <span className="font-semibold text-rotary-ink" data-testid="profile-progress-pct">{progress}%</span>
                                </div>
                                <Progress value={progress} className="h-2 bg-muted [&>div]:rotary-progress-fill" />
                            </div>
                        </div>
                    </div>

                    {/* Action bar */}
                    <div className="mt-5 flex flex-wrap gap-2 border-t pt-4">
                        {!canWrite && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-rotary-gold/15 text-rotary-ink px-3 py-1 text-[11px]" data-testid="profile-readonly-chip">
                                Read-only year — edits disabled
                            </span>
                        )}
                        {mode === "view" ? (
                            <>
                                {canWrite && (
                                    <Button
                                        onClick={() => setMode("edit")}
                                        className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                                        data-testid="profile-edit-btn"
                                    >
                                        <Pencil className="mr-1.5 h-4 w-4" /> Edit full profile
                                    </Button>
                                )}
                                {canWrite && (
                                    <Button
                                        variant="outline"
                                        onClick={onEditCommittees}
                                        className="rounded-full"
                                        data-testid="profile-edit-committees-btn"
                                    >
                                        <Users2 className="mr-1.5 h-4 w-4" /> Edit committees
                                    </Button>
                                )}
                                {canWrite && onToggleVerified && (
                                    <Button
                                        variant="outline"
                                        onClick={() => onToggleVerified(member)}
                                        className={`rounded-full ${yr.verified_at ? "border-emerald-500 text-emerald-700 hover:bg-emerald-500 hover:text-white" : "border-rotary-blue/40 text-rotary-blue hover:bg-rotary-blue hover:text-white"}`}
                                        data-testid="profile-toggle-verified-btn"
                                        title={yr.verified_at ? "Remove verified flag" : "Mark this entry as data-verified"}
                                    >
                                        {yr.verified_at ? (
                                            <><ShieldCheck className="mr-1.5 h-4 w-4" /> Unmark verified</>
                                        ) : (
                                            <><ShieldCheck className="mr-1.5 h-4 w-4" /> Mark verified</>
                                        )}
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    onClick={copyFormLink}
                                    disabled={!formToken}
                                    className="rounded-full"
                                    data-testid="profile-copy-link-btn"
                                    title={formToken ? "Copy the member's personal form link" : "No form link generated for this member yet"}
                                >
                                    <Copy className="mr-1.5 h-4 w-4" /> Copy form link
                                </Button>
                                {canWrite && (
                                    <Button
                                        variant="default"
                                        onClick={openManualWhatsApp}
                                        disabled={!yrid}
                                        className={`rounded-full px-4 ${yrid ? "bg-[#25D366] text-white hover:bg-[#1ebe57]" : ""}`}
                                        data-testid="profile-whatsapp-btn"
                                        title={yrid ? "Open WhatsApp Web with a governor-signed profile reminder pre-composed" : "No form link generated for this member yet"}
                                    >
                                        <WhatsAppIcon className="mr-1.5 h-4 w-4" />
                                        WhatsApp
                                    </Button>
                                )}
                                {canWrite && (
                                    <Button
                                        variant="outline"
                                        onClick={openManualWhatsApp}
                                        disabled={!yrid}
                                        className="rounded-full"
                                        data-testid="profile-remind-btn"
                                        title={yrid ? "Open WhatsApp Web with a governor-signed profile reminder pre-composed" : "No form link generated for this member yet"}
                                    >
                                        <Send className="mr-1.5 h-4 w-4" /> Send reminder
                                    </Button>
                                )}
                                {canWrite && onRemove && (
                                    <Button
                                        variant="outline"
                                        onClick={onRemove}
                                        className="rounded-full text-rotary-cranberry border-rotary-cranberry/40 hover:bg-rotary-cranberry hover:text-white ml-auto"
                                        data-testid="profile-remove-btn"
                                    >
                                        <Trash2 className="mr-1.5 h-4 w-4" /> Remove from year
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button
                                    onClick={save}
                                    disabled={saving}
                                    className="rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                                    data-testid="profile-save-btn"
                                >
                                    {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                                    Save changes
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setMode("view")}
                                    className="rounded-full"
                                    disabled={saving}
                                    data-testid="profile-cancel-btn"
                                >
                                    <X className="mr-1.5 h-4 w-4" /> Cancel
                                </Button>
                            </>
                        )}
                    </div>
                </section>

                {/* Details / editable fields */}
                <section className="grid gap-3 sm:grid-cols-2 mt-5">
                    {mode === "view" ? (
                        <>
                            <DetailRow label="Name" value={member.name} testid="profile-view-name" />
                            <DetailRow label="Phone" value={member.phone} testid="profile-view-phone" />
                            <DetailRow label="Email" value={member.email} testid="profile-view-email" />
                            <DetailRow label="Birthday" value={formatDDMon(member.birthday)} testid="profile-view-birthday" />
                            <div className="sm:col-span-2">
                                <DetailRow label="Classification" value={member.classification} testid="profile-view-classification" />
                            </div>
                            <div className="sm:col-span-2">
                                <DetailRow label="Communication address" value={member.address || member.postal_address} testid="profile-view-address" />
                            </div>
                        </>
                    ) : (
                        <>
                            <FormField label="Name" testid="profile-edit-name">
                                <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-edit-name-input" />
                            </FormField>
                            <FormField label="Phone" testid="profile-edit-phone">
                                <Input
                                    value={form.phone || ""}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D+/g, "").slice(-10) })}
                                    inputMode="numeric"
                                    maxLength={10}
                                    placeholder="9876543210"
                                    data-testid="profile-edit-phone-input"
                                />
                                {form.phone && form.phone.length !== 10 && (
                                    <p className="mt-1 text-xs text-red-600" data-testid="profile-edit-phone-error">Phone must be exactly 10 digits (no country code).</p>
                                )}
                            </FormField>
                            <FormField label="Email" testid="profile-edit-email">
                                <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-edit-email-input" />
                            </FormField>
                            <FormField label="Home Club" testid="profile-edit-club">
                                <Input
                                    value={form.club_name || ""}
                                    onChange={(e) => setForm({ ...form, club_name: e.target.value })}
                                    placeholder="e.g. Rotary Club of Bangalore Midtown"
                                    data-testid="profile-edit-club-input"
                                />
                            </FormField>
                            <FormField label="Birthday" testid="profile-edit-birthday">
                                <DayMonthPicker value={form.birthday || ""} onChange={(v) => setForm({ ...form, birthday: v })} testid="profile-edit-birthday" />
                            </FormField>
                            <div className="sm:col-span-2">
                                <FormField label="Classification" testid="profile-edit-classification">
                                    <Input
                                        value={form.classification || ""}
                                        onChange={(e) => setForm({ ...form, classification: e.target.value.slice(0, 70) })}
                                        maxLength={70}
                                        placeholder="e.g. Chartered Accountant, Industrialist"
                                        data-testid="profile-edit-classification-input"
                                    />
                                    <p className="mt-1 text-[11px] text-muted-foreground text-right" data-testid="profile-edit-classification-counter">
                                        {(form.classification || "").length}/70
                                    </p>
                                </FormField>
                            </div>
                            <div className="sm:col-span-2">
                                <FormField label="Communication address" testid="profile-edit-address">
                                    <Textarea
                                        rows={2}
                                        value={form.postal_address || ""}
                                        onChange={(e) => setForm({ ...form, postal_address: e.target.value })}
                                        data-testid="profile-edit-address-input"
                                    />
                                </FormField>
                            </div>
                        </>
                    )}
                </section>

                {/* Spouse block */}
                <section className="rounded-2xl border bg-white p-5 mt-5">
                    <div className="flex items-center justify-between">
                        <span className="label-eyebrow">Spouse details</span>
                    </div>
                    {/* Iter 100p · photo on top (like the member section), fields
                        in a wide 2-column grid below. The old
                        `grid-cols-[auto_1fr]` layout put the photo on the left
                        and crammed every field into a narrow right column,
                        which made the two new spouse contact inputs
                        unreadable on desktop. */}
                    <div className="mt-4 flex items-start gap-4">
                        {mode === "edit" ? (
                            <PhotoUploadCrop
                                subject="spouse"
                                previewSrc={spouseSrc}
                                onCropped={(file) => acceptCroppedPhoto(file, true)}
                                onRemove={spouseSrc ? (() => { setSpousePhotoFile(null); setSpousePhotoPreview(""); setRemoveSpousePhoto(true); }) : undefined}
                                testid="profile-edit-spouse-photo"
                                yearId={yr.id || yr._id || ""}
                            />
                        ) : (
                            <PhotoBlock src={spouseSrc} name={member.spouse_name || "Spouse"} testid="profile-spouse-photo" />
                        )}
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {mode === "view" ? (
                            <>
                                <div className="sm:col-span-2">
                                    <DetailRow label="Spouse name" value={member.spouse_name} testid="profile-view-spouse-name" />
                                </div>
                                <DetailRow label="Spouse birthday" value={formatDDMon(member.spouse_birthday)} testid="profile-view-spouse-birthday" />
                                <DetailRow label="Anniversary" value={formatDDMon(member.spouse_anniversary || member.anniversary)} testid="profile-view-anniversary" />
                                {/* Iter 100m · Spouse contact tiles.
                                    Rendered for every member who already
                                    has a spouse name, EVEN when the
                                    contact fields themselves are still
                                    blank — the tiles act as an
                                    affordance so admins know they can
                                    add these values via Edit full
                                    profile. Members with no spouse
                                    avoid empty-tile clutter. */}
                                {(member.spouse_name || "").trim() && (
                                    <>
                                        <DetailRow label="Spouse phone" value={member.spouse_phone} testid="profile-view-spouse-phone" />
                                        <DetailRow label="Spouse email" value={member.spouse_email} testid="profile-view-spouse-email" />
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Iter 100q · spouse name spans both columns; birthday & anniversary sit below in 2 cols; phone & email below that. */}
                                <div className="sm:col-span-2">
                                    <FormField label="Spouse name" testid="profile-edit-spouse-name">
                                        <Input value={form.spouse_name || ""} onChange={(e) => setForm({ ...form, spouse_name: e.target.value })} data-testid="profile-edit-spouse-name-input" />
                                    </FormField>
                                </div>
                                <FormField label="Spouse birthday" testid="profile-edit-spouse-birthday">
                                    <DayMonthPicker value={form.spouse_birthday || ""} onChange={(v) => setForm({ ...form, spouse_birthday: v })} testid="profile-edit-spouse-birthday" />
                                </FormField>
                                <FormField label="Anniversary" testid="profile-edit-anniversary">
                                    <DayMonthPicker value={form.anniversary || ""} onChange={(v) => setForm({ ...form, anniversary: v })} testid="profile-edit-anniversary" />
                                </FormField>
                                {/* Iter 100j · optional spouse contact fields.
                                    Only shown when a spouse name is set. */}
                                {(form.spouse_name || "").trim() && (
                                    <>
                                        <FormField label="Spouse phone (optional)" testid="profile-edit-spouse-phone">
                                            <Input
                                                value={form.spouse_phone || ""}
                                                onChange={(e) => setForm({ ...form, spouse_phone: e.target.value })}
                                                inputMode="tel"
                                                maxLength={20}
                                                placeholder="e.g. 9876543210"
                                                data-testid="profile-edit-spouse-phone-input"
                                            />
                                        </FormField>
                                        <FormField label="Spouse email (optional)" testid="profile-edit-spouse-email">
                                            <Input
                                                type="email"
                                                value={form.spouse_email || ""}
                                                onChange={(e) => setForm({ ...form, spouse_email: e.target.value })}
                                                placeholder="spouse@example.com"
                                                data-testid="profile-edit-spouse-email-input"
                                            />
                                        </FormField>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </section>

                {/* Form link footer (view mode) */}
                {mode === "view" && formToken && (
                    <section className="mt-5 rounded-xl border bg-muted/30 p-3 flex items-center gap-2 text-xs text-muted-foreground">
                        <LinkIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate" data-testid="profile-form-url">{`${window.location.origin}/form/${formToken}`}</span>
                    </section>
                )}

                {/* Visiting-card QR (view mode only, requires 100% profile / share_token) */}
                {mode === "view" && member.share_token && (
                    <section
                        className="mt-4 rounded-xl border-2 border-rotary-gold/40 bg-gradient-to-br from-rotary-gold/5 via-white to-rotary-blue/5 p-4"
                        data-testid="profile-visiting-card"
                    >
                        <div className="flex items-start gap-4">
                            <div className="rounded-lg bg-white p-2 border shrink-0">
                                <QRCodeSVG
                                    value={`${window.location.origin}/card/${member.share_token}`}
                                    size={96}
                                    level="H"
                                    includeMargin={false}
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <span className="label-eyebrow text-rotary-gold flex items-center gap-1.5">
                                    <QrCode className="h-3 w-3" /> Visiting card
                                </span>
                                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                                    Scannable card at{" "}
                                    <a
                                        href={`/card/${member.share_token}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-rotary-blue hover:underline"
                                        data-testid="profile-card-link"
                                    >
                                        /card/{member.share_token.slice(0, 8)}…
                                    </a>
                                    . Save as PNG for printed directories.
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={downloadQR}
                                        className="rounded-full h-8 text-xs"
                                        data-testid="profile-download-qr-btn"
                                    >
                                        <Download className="mr-1.5 h-3.5 w-3.5" />
                                        Download PNG
                                    </Button>
                                    <a
                                        href={`/card/${member.share_token}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center rounded-full border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 px-3 text-xs font-medium"
                                        data-testid="profile-open-card-btn"
                                    >
                                        Preview card →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>
                )}
            </DialogContent>
        </Dialog>
    );
}

function FormField({ label, testid, children }) {
    return (
        <div data-testid={testid}>
            <Label className="label-eyebrow">{label}</Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}
